import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function FAQPage() {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqs = [
    {
      category: "For Guests",
      questions: [
        {
          question: "How do I book a short-term rental with ARM?",
          answer: "You can book through our website using the booking form, call us at 732-213-2063, or email us at Staying.arm@gmail.com. We'll check availability and provide you with pricing and next steps."
        },
        {
          question: "What is included in ARM short-term rentals?",
          answer: "All our short-term rentals come fully furnished with hotel-quality amenities including WiFi, fully equipped kitchen, linens, towels, cleaning supplies, and basic toiletries. Each property also includes 24/7 support and secure self-check-in."
        },
        {
          question: "Is there a minimum stay requirement?",
          answer: "Our minimum stay is typically 1-3 nights depending on the property and season. Extended stays of 7+ nights often qualify for discounted rates."
        },
        {
          question: "What is your cancellation policy?",
          answer: "We offer flexible cancellation policies that vary by booking type and timing. Full details will be provided at the time of booking confirmation."
        },
        {
          question: "Do you provide 24/7 support during my stay?",
          answer: "Yes! ARM provides round-the-clock support for all guests. You can reach us anytime via phone, text, or our AI assistant for immediate assistance."
        }
      ]
    },
    {
      category: "For Property Owners",
      questions: [
        {
          question: "How does ARM's guaranteed rent program work?",
          answer: "ARM pays you a fixed monthly rent regardless of occupancy. We handle all guest management, cleaning, maintenance, and operations while you receive consistent income without any of the hassles."
        },
        {
          question: "What type of properties does ARM accept?",
          answer: "We work with single-family homes, townhouses, apartments, and condos in good condition. Properties should be in desirable locations and suitable for short-term or long-term rentals."
        },
        {
          question: "How do you protect my property?",
          answer: "We use smart noise monitoring, conduct regular inspections, provide professional cleaning between stays, screen all guests thoroughly, and maintain comprehensive insurance coverage."
        },
        {
          question: "What are ARM's fees for property management?",
          answer: "Our fee structure varies based on property type, location, and services required. We'll provide a customized proposal during our property evaluation process."
        },
        {
          question: "How long is the partnership agreement?",
          answer: "We typically offer 2-5 year agreements with options for renewal. Terms are flexible and designed to benefit both parties long-term."
        }
      ]
    },
    {
      category: "General Information",
      questions: [
        {
          question: "Where does ARM operate?",
          answer: "ARM operates throughout Pennsylvania, providing professional residential management services across the state."
        },
        {
          question: "Is ARM a licensed company?",
          answer: "Yes, Ali Residential Management is a registered LLC operating in full compliance with local and state regulations. We maintain all necessary licenses and insurance coverage."
        },
        {
          question: "Do you offer long-term rentals?",
          answer: "While we currently specialize in short-term rentals, we are actively building our long-term rental portfolio. Contact us to be notified when long-term options become available."
        },
        {
          question: "How can I contact ARM?",
          answer: "You can reach us by phone/text at 732-213-2063, email at Staying.arm@gmail.com, through our website contact forms, or via our AI chat assistant available on every page."
        },
        {
          question: "What makes ARM different from other rental companies?",
          answer: "ARM combines the personalized service of a local company with professional systems and technology. We offer guaranteed rent to owners, hotel-quality standards for guests, and 24/7 support for everyone."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 slide-up">
            Frequently Asked Questions
          </h1>
          <p className="text-xl md:text-2xl text-teal-50 max-w-3xl mx-auto slide-up">
            Find answers to common questions about ARM's services
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex} className="slide-up" style={{ animationDelay: `${categoryIndex * 0.1}s` }}>
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  {category.category}
                </h2>
                
                <div className="space-y-4">
                  {category.questions.map((faq, faqIndex) => {
                    const itemIndex = categoryIndex * 100 + faqIndex;
                    const isOpen = openItems.includes(itemIndex);
                    
                    return (
                      <div key={faqIndex} className="bg-white rounded-lg shadow-md border border-gray-200">
                        <button
                          onClick={() => toggleItem(itemIndex)}
                          className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-all duration-300 cursor-pointer"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 pr-4">
                            {faq.question}
                          </h3>
                          <div className={`w-8 h-8 flex items-center justify-center transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : ''
                          }`}>
                            <i className="ri-arrow-down-s-line text-2xl text-teal-600"></i>
                          </div>
                        </button>
                        
                        <div className={`overflow-hidden transition-all duration-300 ${
                          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}>
                          <div className="px-6 pb-4">
                            <div className="border-t border-gray-200 pt-4">
                              <p className="text-gray-700 leading-relaxed">
                                {faq.answer}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-xl text-teal-50 mb-8 leading-relaxed">
            Our team is here to help! Contact us directly or use our AI assistant for immediate answers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="sms:732-213-2063"
              className="px-8 py-4 bg-white text-purple-700 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
            >
              <i className="ri-message-3-line mr-2"></i>
              Text Us: 732-213-2063
            </a>
            <a
              href="mailto:Staying.arm@gmail.com"
              className="px-8 py-4 bg-purple-800 text-white font-semibold rounded-lg hover:bg-purple-900 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
            >
              <i className="ri-mail-line mr-2"></i>
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Structured Data for FAQ */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.flatMap(category => 
            category.questions.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          )
        })
      }} />
    </div>
  );
}
